import React from "react";
import {
  Avatar,
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { 
  Folder, 
  Visibility, 
  Edit, 
  Delete,
  LocalPharmacy,
  MedicalServices,
  Receipt,
  Payment,
  Schedule,
  MoreHoriz,
  AccountBalance,
  Notifications,
  Assignment
} from "@mui/icons-material";
import { DocumentDto } from "../../api-client";

interface DocumentListItemProps {
  document: DocumentDto;
  index?: number;
  onSecureView: (document: DocumentDto) => void;
  onEdit: (document: DocumentDto) => void;
  onDelete: (document: DocumentDto) => void;
}

// Function to get the appropriate icon for each document type
const getDocumentIcon = (docType: string) => {
  switch (docType) {
    case "Rezept":
      return <LocalPharmacy />;
    case "Medizinisch":
      return <MedicalServices />;
    case "Rechnung":
      return <Receipt />;
    case "Quittung":
      return <Payment />;
    case "Termine":
      return <Schedule />;
    case "Sonstiges":
      return <MoreHoriz />;
    case "Mandat":
      return <AccountBalance />;
    case "Mitteilung":
      return <Notifications />;
    case "Arztbericht":
      return <Assignment />;
    default:
      return <Folder />;
  }
};

export const DocumentListItem: React.FC<DocumentListItemProps> = ({
  document,
  index,
  onSecureView,
  onEdit,
  onDelete,
}) => {
  // Format creation date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const fileName = document.label || document.name?.split('/').pop() || '';
  const creationDate = formatDate(document.createdAt);
  const secondaryText = creationDate ? `${creationDate} • ${fileName}` : fileName;
  return (
    <ListItem
      key={document.id || index}
      sx={{ 
        flexWrap: 'wrap',
        paddingRight: '130px', // Space for action buttons
        position: 'relative'
      }}
      secondaryAction={
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'nowrap',
          position: 'absolute', 
          right: '8px', 
          top: '50%',
          transform: 'translateY(-50%)'
        }}>
          <IconButton
            size="small"
            aria-label="secure-view"
            onClick={() => onSecureView(document)}
            title="Sicher anzeigen"
            sx={{ mx: 0.5 }}
          >
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="edit"
            onClick={() => onEdit(document)}
            title="Bearbeiten"
            sx={{ mx: 0.5 }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="delete"
            onClick={() => onDelete(document)}
            title="Löschen"
            sx={{ mx: 0.5 }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      }
    >
      <ListItemAvatar>
        <Avatar>
          {getDocumentIcon(document.docuType)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText 
        primary={document.docuType} 
        secondary={secondaryText}
        sx={{ 
          flex: 1,
          minWidth: 0, // Allows flex item to shrink below its content size
          '& .MuiListItemText-secondary': { 
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          } 
        }}
      />
    </ListItem>
  );
};

export default DocumentListItem;
